import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Moon, Sun } from "lucide-react";

import { StatusFlag } from "@/components/console/StatusFlag";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useWizardFlow } from "@/hooks/useWizardFlow";
import { useBridgeStatus, useWizardState } from "@/hooks/wizardQueries";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/cn";
import { formatTimestamp } from "@/lib/format";
import { strings } from "@/lib/strings";
import { WIZARD_STEPS, wizardLocationPath, wizardRoutePath } from "@/lib/steps";

export function Topbar() {
  const stateQuery = useWizardState();
  const statusQuery = useBridgeStatus();
  const flow = useWizardFlow();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const wizardState = stateQuery.data;
  const status = statusQuery.data;
  const pendingReason = wizardState?.pendingReason ?? status?.pendingReason;

  const activeIndex = WIZARD_STEPS.findIndex((step) => {
    const absolute = wizardLocationPath(step.segment);
    return location.pathname === absolute || location.pathname.startsWith(`${absolute}/`);
  });

  return (
    <header className="sticky top-0 z-30 flex flex-col gap-3 border-b border-border/60 bg-background/80 px-6 py-4 backdrop-blur-xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {stateQuery.isLoading ? (
            <Skeleton className="h-6 w-48" />
          ) : (
            <>
              <StatusFlag
                label={
                  wizardState?.firstStartPending
                    ? strings.flags.firstStartPending
                    : strings.flags.firstStartCompleted
                }
                variant={wizardState?.firstStartPending ? "warning" : "success"}
              />
              <StatusFlag
                label={
                  wizardState?.updatePending ? strings.flags.updatesPending : strings.flags.updatesClean
                }
                variant={wizardState?.updatePending ? "warning" : "success"}
              />
              <StatusFlag
                label={
                  wizardState?.wizardRequiredMode
                    ? strings.flags.modeWizardRequired
                    : strings.flags.modeAutomatic
                }
                variant={wizardState?.wizardRequiredMode ? "accent" : "muted"}
              />
              {wizardState?.phase ? (
                <StatusFlag label={`${strings.flags.phase}: ${wizardState.phase}`} variant="outline" />
              ) : null}
              {status?.organizationName || status?.organizationId ? (
                <StatusFlag
                  label={[
                    status.organizationName ?? status.organizationId,
                    status.projectName ?? status.projectId,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  variant="outline"
                />
              ) : null}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" aria-label="Toggle theme" onClick={toggle}>
            {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </Button>
        </div>
      </div>

      {pendingReason ? (
        <p className="font-mono text-xs text-warning">
          {strings.flags.pendingReason}: {pendingReason}
        </p>
      ) : null}

      <nav className="flex gap-1 overflow-x-auto lg:hidden" aria-label="Console sections">
        <Link
          to="/"
          className={cn(
            "shrink-0 rounded-md border px-2 py-1 text-[10px] font-mono uppercase tracking-wide transition-colors",
            location.pathname === "/" || location.pathname === "/live"
              ? "border-primary bg-primary/10 text-foreground"
              : "border-border text-muted-foreground",
          )}
        >
          {strings.nav.live}
        </Link>
        {WIZARD_STEPS.map((step, index) => {
          const stepFlow = flow.steps[step.key];
          if (!stepFlow.visible) {
            return null;
          }

          const isActive = index === activeIndex;
          const isDone = stepFlow.completed && !isActive;
          const canNavigate = stepFlow.accessible || isDone;
          const pillClass = cn(
            "shrink-0 rounded-md border px-2 py-1 text-[10px] font-mono uppercase tracking-wide transition-colors",
            isActive && "border-primary bg-primary/10 text-foreground",
            isDone && !isActive && "border-success/50 text-success",
            !isActive && !isDone && canNavigate && "border-border text-muted-foreground",
            !canNavigate && "cursor-not-allowed border-border/50 text-muted-foreground/50",
          );

          if (!canNavigate) {
            return (
              <span key={step.key} className={pillClass} aria-disabled>
                {step.title}
              </span>
            );
          }

          return (
            <Link key={step.key} to={wizardRoutePath(step.segment)} className={pillClass}>
              {step.title}
            </Link>
          );
        })}
      </nav>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {strings.flags.lastApplied}:{" "}
          <span className="font-mono text-foreground">{formatTimestamp(wizardState?.lastAppliedAt)}</span>
          {wizardState?.lastOperator ? (
            <>
              {" "}
              · <span className="text-foreground">{wizardState.lastOperator}</span>
            </>
          ) : null}
        </span>
        <span className="font-mono mono-num text-foreground/70">{now.toISOString()}</span>
      </div>
    </header>
  );
}
